import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { API_CONFIG } from '../config/api';
import axios from 'axios';
import {
  Table,
  Card,
  Button,
  Tag,
  Space,
  Input,
  Modal,
  Form,
  message,
  Statistic,
  Row,
  Col,
  Descriptions,
  Badge,
  Tooltip,
  Popconfirm,
  Typography,
  Upload,
  Image,
  Divider,
  List,
  Empty,
  Drawer,
  Flex,
  Spin
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  ReloadOutlined,
  SettingOutlined,
  UploadOutlined,
  QrcodeOutlined,
  CopyOutlined,
  BankOutlined,
  FilterOutlined,
  SearchOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DepositApprovals = () => {
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [approvalVisible, setApprovalVisible] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [form] = Form.useForm();
  const [settingsForm] = Form.useForm();
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [viewMode, setViewMode] = useState(window.innerWidth <= 768 ? 'cards' : 'table');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: null
  });

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      setViewMode(mobile ? 'cards' : 'table');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchPendingDeposits();
    fetchPaymentSettings();
  }, []);

  const fetchPendingDeposits = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/payment/deposits/pending');
      setPendingDeposits(response.data.data.transactions || []);
    } catch (error) {
      message.error('Failed to fetch pending deposits');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await api.get('/api/payment/settings');
      setPaymentSettings(response.data.data);
      settingsForm.setFieldsValue(response.data.data);
    } catch (error) {
      message.error('Failed to fetch payment settings');
    }
  };

  const handleApproval = async (transactionId, status, reason = '') => {
    try {
      await api.put(`/api/payment/deposits/${transactionId}/status`, {
        status,
        reason
      });
      message.success(`Deposit ${status === 'completed' ? 'approved' : 'rejected'} successfully`);
      setApprovalVisible(false);
      form.resetFields();
      fetchPendingDeposits();
    } catch (error) {
      message.error('Failed to process deposit approval');
    }
  };

  const updatePaymentSettings = async (values) => {
    try {
      await api.put('/api/payment/settings', values);
      message.success('Payment settings updated successfully');
      setSettingsVisible(false);
      fetchPaymentSettings();
    } catch (error) {
      message.error('Failed to update payment settings');
    }
  };


  const uploadQRCode = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      message.error('Please upload a valid image file (JPG, PNG, GIF)');
      return false;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      message.error('File size must be less than 5MB');
      return false;
    }

    const formData = new FormData();
    formData.append('qrImage', file);

    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.post(
        `${API_CONFIG.BASE_URL}/api/payment/settings/qr-upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      message.success('QR code uploaded successfully');
      await fetchPaymentSettings();
      return false;
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to upload QR code';
      message.error(errorMessage);
      return false;
    }
  };

  const copyUpiId = () => {
    if (paymentSettings?.upiId) {
      navigator.clipboard.writeText(paymentSettings.upiId);
      message.success('UPI ID copied to clipboard');
    }
  };

  // Helper functions for styling
  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      processing: 'blue',
      completed: 'green',
      failed: 'red'
    };
    return colors[status] || 'default';
  };

  // Filter deposits based on search and filters
  const filteredDeposits = pendingDeposits.filter(deposit => {
    const matchesSearch = !filters.search || 
      deposit.userId?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
      deposit.userId?.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
      deposit.upiDetails?.utrNumber?.toLowerCase().includes(filters.search.toLowerCase());
    
    const matchesStatus = !filters.status || deposit.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: '_id',
      key: '_id',
      render: (text) => (
        <Text code copyable={{ text }}>
          {text.slice(-8)}
        </Text>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'user',
      render: (user) => (
        <div>
          <div><strong>{user?.name}</strong></div>
          <div><Text type="secondary">{user?.email}</Text></div>
        </div>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Text strong style={{ color: '#52c41a' }}>
          ₹{amount?.toLocaleString()}
        </Text>
      ),
    },
    {
      title: 'UTR Number',
      dataIndex: 'upiDetails',
      key: 'utrNumber',
      render: (upiDetails) => (
        <Text code copyable={{ text: upiDetails?.utrNumber }}>
          {upiDetails?.utrNumber}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          pending: 'orange',
          processing: 'blue',
          completed: 'green',
          failed: 'red',
        };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Submitted At',
      dataIndex: 'initiatedAt',
      key: 'initiatedAt',
      render: (date) => dayjs(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedDeposit(record);
                setDetailsVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Approve">
            <Popconfirm
              title="Approve this deposit?"
              description="This will add money to user's wallet"
              onConfirm={() => handleApproval(record._id, 'completed')}
              okText="Approve"
              cancelText="Cancel"
            >
              <Button
                type="text"
                icon={<CheckOutlined />}
                style={{ color: '#52c41a' }}
              />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Reject">
            <Button
              type="text"
              icon={<CloseOutlined />}
              style={{ color: '#ff4d4f' }}
              onClick={() => {
                setSelectedDeposit(record);
                setApprovalVisible(true);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loading && pendingDeposits.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="fade-in" style={{ padding: '0 4px' }}>
      {/* Modern Header */}
      <div className="deposit-approvals-header" style={{
        background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
        borderRadius: isMobile ? '20px' : '24px',
        padding: isMobile ? '24px 20px' : '40px 32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(114, 46, 209, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '200px',
          height: '200px',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: '50%',
          transform: 'translate(50%, -50%)'
        }} />
        
        <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
          <div style={{ zIndex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '16px',
                padding: '12px',
                backdropFilter: 'blur(10px)'
              }}>
                <BankOutlined style={{ fontSize: isMobile ? '24px' : '28px' }} />
              </div>
              <Title level={isMobile ? 3 : 1} style={{ 
                margin: 0, 
                color: 'white', 
                fontSize: isMobile ? '22px' : '32px', 
                fontWeight: 800,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                letterSpacing: '-0.5px'
              }}>
                Deposit Approvals
              </Title>
            </div>
            <Text style={{ 
              color: 'rgba(255,255,255,0.9)', 
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: 500
            }}>
              Review and approve pending deposit requests
            </Text>
          </div>
          <Space size={isMobile ? 8 : 12}>
            {!isMobile && (
              <>
                <Button
                  icon={<DownloadOutlined />}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Export
                </Button>
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setSettingsVisible(true)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    color: 'white',
                    borderRadius: '12px',
                    height: '44px',
                    fontWeight: '600'
                  }}
                >
                  Settings
                </Button>
              </>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchPendingDeposits}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: 'white',
                borderRadius: '12px',
                height: isMobile ? '40px' : '44px',
                fontWeight: '600'
              }}
              loading={loading}
            >
              Refresh
            </Button>
          </Space>
        </Flex>
      </div>

      {/* Enhanced Statistics Cards */}
      <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 16]} style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Col xs={12} sm={12} md={8} lg={8}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #faad14 0%, #ffc53d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              ⏳
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Pending Deposits
                </span>
              }
              value={pendingDeposits.length}
              precision={0}
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '18px' : '24px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={12} md={8} lg={8}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              💰
            </div>
            <Statistic
              title={
                <span style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px'
                }}>
                  Total Amount
                </span>
              }
              value={pendingDeposits.reduce((sum, deposit) => sum + deposit.amount, 0)}
              precision={2}
              prefix="₹"
              valueStyle={{ 
                color: 'white', 
                fontSize: isMobile ? '16px' : '22px', 
                fontWeight: 'bold',
                lineHeight: 1.2
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8}>
          <Card 
            className="slide-up stats-card-modern" 
            style={{
              background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
              border: 'none',
              borderRadius: isMobile ? '16px' : '20px',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              minHeight: isMobile ? '100px' : '120px',
              height: isMobile ? '100px' : '120px'
            }}
            bodyStyle={{ 
              padding: isMobile ? '16px' : '20px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <div style={{
              position: 'absolute',
              top: isMobile ? '-5px' : '-10px',
              right: isMobile ? '-5px' : '-10px',
              fontSize: isMobile ? '40px' : '60px',
              opacity: 0.2,
              color: 'white'
            }}>
              🏦
            </div>
            <Flex justify="space-between" align="center">
              <div>
                <div style={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  fontWeight: 600,
                  fontSize: isMobile ? '11px' : '14px',
                  marginBottom: 4
                }}>
                  Current UPI ID
                </div>
                <div style={{
                  color: 'white', 
                  fontSize: isMobile ? '14px' : '16px', 
                  fontWeight: 'bold',
                  wordBreak: 'break-all'
                }}>
                  {paymentSettings?.upiId || 'Not Set'}
                </div>
              </div>
              <Button
                type="text"
                icon={<CopyOutlined />}
                onClick={copyUpiId}
                style={{ color: 'white' }}
                size={isMobile ? 'small' : 'middle'}
              />
            </Flex>
          </Card>
        </Col>
      </Row>

      {/* Mobile-Friendly Search and Actions */}
      {isMobile ? (
        <>
          <Card 
            style={{ 
              marginBottom: 20, 
              borderRadius: '20px',
              border: 'none',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)'
            }} 
            className="slide-up"
            bodyStyle={{ padding: '20px' }}
          >
            <div style={{ marginBottom: 16 }}>
              <Input
                placeholder="🔍 Search deposits by user, UTR, or amount..."
                allowClear
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{ 
                  borderRadius: '16px',
                  border: '2px solid #f1f5f9',
                  background: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  fontSize: '16px',
                  height: '48px'
                }}
                size="large"
              />
            </div>
            <Flex gap={8}>
              <Button
                block
                icon={<FilterOutlined />}
                onClick={() => setShowFilters(!showFilters)}
                style={{ 
                  borderRadius: '16px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: showFilters 
                    ? 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)' 
                    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                  borderColor: showFilters ? '#722ed1' : '#e2e8f0',
                  color: showFilters ? 'white' : '#475569',
                  border: 'none',
                  boxShadow: showFilters 
                    ? '0 4px 12px rgba(114, 46, 209, 0.3)' 
                    : '0 2px 8px rgba(0,0,0,0.06)',
                  transition: 'all 0.3s ease'
                }}
              >
                {showFilters ? '🔽 Hide Filters' : '🔼 Show Filters'}
              </Button>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsVisible(true)}
                style={{
                  borderRadius: '16px',
                  height: '48px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #722ed1 0%, #9254de 100%)',
                  border: 'none',
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)',
                  minWidth: '60px'
                }}
              />
            </Flex>
          </Card>
        </>
      ) : (
        /* Desktop Search and Filters */
        <Card style={{ marginBottom: 24, borderRadius: '16px' }}>
          <Row gutter={16} align="middle">
            <Col span={8}>
              <Input
                placeholder="Search deposits..."
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{ borderRadius: '8px' }}
              />
            </Col>
            <Col span={6}>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setSettingsVisible(true)}
                style={{ borderRadius: '8px' }}
              >
                Payment Settings
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Deposits List/Table */}
      {viewMode === 'cards' ? (
        /* Mobile Deposit Cards */
        <div>
          {filteredDeposits.length === 0 ? (
            <Card style={{ textAlign: 'center', padding: '40px', borderRadius: '20px' }}>
              <Empty
                description="No pending deposits found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Card>
          ) : (
            <List
              dataSource={filteredDeposits}
              renderItem={(deposit) => (
                <List.Item
                  key={deposit._id}
                  style={{
                    padding: '16px',
                    marginBottom: '12px',
                    background: 'white',
                    borderRadius: '16px',
                    border: '1px solid #f1f5f9',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                >
                  <div style={{ width: '100%' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: 12
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                          {deposit.userId?.name || 'Unknown User'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {deposit.userId?.email || 'No email'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Tag 
                          color={getStatusColor(deposit.status)}
                          style={{ 
                            borderRadius: '20px',
                            fontWeight: '600',
                            fontSize: '12px',
                            marginBottom: 4
                          }}
                        >
                          {deposit.status?.toUpperCase()}
                        </Tag>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          ID: {deposit._id.slice(-8)}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: 12
                    }}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>
                        🏦 UTR: {deposit.upiDetails?.utrNumber || 'N/A'}
                      </span>
                      <span style={{ 
                        fontSize: '18px', 
                        fontWeight: '700',
                        color: '#52c41a'
                      }}>
                        ₹{deposit.amount?.toLocaleString() || '0'}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      paddingTop: 12,
                      borderTop: '1px solid #f1f5f9'
                    }}>
                      <span style={{ fontSize: '12px', color: '#64748b' }}>
                        📅 {dayjs(deposit.initiatedAt).format('DD/MM/YYYY HH:mm')}
                      </span>
                      <Space size={8}>
                        <Button
                          type="link"
                          size="small"
                          icon={<EyeOutlined />}
                          onClick={() => {
                            setSelectedDeposit(deposit);
                            setDetailsVisible(true);
                          }}
                          style={{ padding: 0, height: 'auto' }}
                        >
                          View
                        </Button>
                        <Popconfirm
                          title="Approve this deposit?"
                          description="This will add money to user's wallet"
                          onConfirm={() => handleApproval(deposit._id, 'completed')}
                          okText="Approve"
                          cancelText="Cancel"
                        >
                          <Button
                            type="link"
                            size="small"
                            icon={<CheckOutlined />}
                            style={{ padding: 0, height: 'auto', color: '#52c41a' }}
                          >
                            Approve
                          </Button>
                        </Popconfirm>
                        <Button
                          type="link"
                          size="small"
                          icon={<CloseOutlined />}
                          onClick={() => {
                            setSelectedDeposit(deposit);
                            setApprovalVisible(true);
                          }}
                          style={{ padding: 0, height: 'auto', color: '#ff4d4f' }}
                        >
                          Reject
                        </Button>
                      </Space>
                    </div>
                  </div>
                </List.Item>
              )}
              pagination={{
                pageSize: 8,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} deposits`,
                style: { textAlign: 'center', marginTop: 16 }
              }}
            />
          )}
        </div>
      ) : (
        /* Desktop Deposits Table */
        <Card style={{ borderRadius: '16px' }}>
          <Table
            columns={columns}
            dataSource={filteredDeposits}
            rowKey="_id"
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} deposits`
            }}
            scroll={{ x: 1200 }}
          />
        </Card>
      )}

      {/* Deposit Details Modal */}
      <Modal
        title="💳 Deposit Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={isMobile ? '100%' : 600}
        style={{ top: isMobile ? 0 : 20 }}
      >
        {selectedDeposit && (
          <Descriptions bordered column={1}>
            <Descriptions.Item label="Transaction ID">
              <Text code copyable={{ text: selectedDeposit._id }}>
                {selectedDeposit._id}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="User">
              <div>
                <div><strong>{selectedDeposit.userId?.name}</strong></div>
                <div>{selectedDeposit.userId?.email}</div>
                <div>{selectedDeposit.userId?.phone}</div>
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ color: '#52c41a', fontSize: '18px' }}>
                ₹{selectedDeposit.amount?.toLocaleString()}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="UTR Number">
              <Text code copyable={{ text: selectedDeposit.upiDetails?.utrNumber }}>
                {selectedDeposit.upiDetails?.utrNumber}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method">
              <Tag color="blue">{selectedDeposit.method?.toUpperCase()}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Badge
                status={selectedDeposit.status === 'pending' ? 'processing' : 'success'}
                text={selectedDeposit.status?.toUpperCase()}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Submitted At">
              {dayjs(selectedDeposit.initiatedAt).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {selectedDeposit.description}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title="❌ Reject Deposit"
        open={approvalVisible}
        onCancel={() => {
          setApprovalVisible(false);
          form.resetFields();
        }}
        onOk={() => {
          form.validateFields().then((values) => {
            handleApproval(selectedDeposit._id, 'failed', values.reason);
          });
        }}
        okText="Reject"
        okButtonProps={{ danger: true }}
        width={isMobile ? '100%' : 500}
        style={{ top: isMobile ? 0 : 20 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="reason"
            label="Rejection Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter reason for rejection..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Payment Settings Modal */}
      <Modal
        title="⚙️ Payment Settings"
        open={settingsVisible}
        onCancel={() => setSettingsVisible(false)}
        onOk={() => {
          settingsForm.validateFields().then((values) => {
            updatePaymentSettings(values);
          });
        }}
        width={isMobile ? '100%' : 700}
        style={{ top: isMobile ? 0 : 20 }}
      >
        <Form form={settingsForm} layout="vertical">
          <Form.Item
            name="upiId"
            label="UPI ID"
            rules={[{ required: true, message: 'Please enter UPI ID' }]}
          >
            <Input
              placeholder="Enter UPI ID (e.g., admin@paytm)"
              suffix={<CopyOutlined onClick={copyUpiId} />}
            />
          </Form.Item>
          
          <Form.Item
            name="merchantName"
            label="Merchant Name"
            rules={[{ required: true, message: 'Please enter merchant name' }]}
          >
            <Input placeholder="Enter merchant name" />
          </Form.Item>

          <Divider>QR Code Management</Divider>
          
          <Form.Item label="Current QR Code">
            {paymentSettings?.qrCodeUrl ? (
                <Image
                  width={200}
                  src={`${API_CONFIG.BASE_URL}${paymentSettings.qrCodeUrl}`}
                  alt="Payment QR Code"
                  style={{ border: '1px solid #d9d9d9', borderRadius: '8px' }}
                />
              
            ) : (
              <div style={{ 
                width: 200, 
                height: 200, 
                border: '2px dashed #d9d9d9', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999'
              }}>
                <QrcodeOutlined style={{ fontSize: '48px' }} />
              </div>
            )}
          </Form.Item>

          <Form.Item label="Upload New QR Code">
            <Upload
              beforeUpload={uploadQRCode}
              accept="image/*"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>
                Upload QR Code
              </Button>
            </Upload>
            <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
              Supported formats: JPG, PNG, GIF (Max 5MB)
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DepositApprovals;
